/**
 * Webメール導線（05-ui.md §8.8）。
 *
 * ディープリンクの URL は **必ずサーバーが組み立てる**
 * （`GET /portal/mailboxes/:id/webmail`）。SPA で URL を組み立てると、
 * ワンタイム性の担保もテナント判定も壊れる。
 *
 * ポップアップブロック対策として、`window.open` はクリックハンドラ内で
 * **同期的に**空タブを開き、URL を取得してから `location` を差し替える。
 */
import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
} from '@mail-sv/ui';
import { ExternalLink, Mail } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { ErrorAlert, LoadingBlock } from '../components/async.js';
import { HelpNote } from '../components/help.js';
import { env } from '../env.js';
import { openPendingTab } from '../lib/open-tab.js';
import type { CursorPage, Mailbox, WebmailLink } from '../lib/api-types.js';

export function WebmailPage() {
  const [error, setError] = React.useState<unknown>(null);

  const mailboxes = useQuery({
    queryKey: qk.mailboxes({ view: 'webmail' }),
    queryFn: () => api.get<CursorPage<Mailbox>>('/mailboxes?limit=200'),
    staleTime: 60_000,
  });

  const open = useMutation({
    mutationFn: (mailboxId: string) =>
      api.get<WebmailLink>(`/mailboxes/${encodeURIComponent(mailboxId)}/webmail`),
  });

  const items = (mailboxes.data?.items ?? []).filter((m) => m.webmailEnabled);

  const handleOpen = (mailboxId: string) => {
    setError(null);
    // クリックと同じイベントループで開かないとブロックされる。
    const tab = openPendingTab();
    open.mutate(mailboxId, {
      onSuccess: (link) => {
        tab.navigate(link.url);
      },
      onError: (e) => {
        tab.close();
        setError(e);
      },
    });
  };

  return (
    <>
      <PageHeader
        title="Webメール"
        description="ブラウザでメールの送受信ができます。メールソフトの設定は不要です。"
      />

      <div className="space-y-5">
        {error ? (
          <div className="space-y-2">
            <ErrorAlert error={error} />
            {env.webmailUrl ? (
              <Alert variant="info">
                <AlertTitle>手動で開く</AlertTitle>
                <AlertDescription>
                  自動でのログインに失敗しました。
                  <a
                    href={env.webmailUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mx-1 text-primary underline-offset-4 hover:underline"
                  >
                    Webメールのログイン画面
                  </a>
                  から、メールアドレスとパスワードでログインしてください。
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        ) : null}

        {mailboxes.isPending ? (
          <LoadingBlock lines={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="Webメールを使えるメールアドレスがありません"
            description="メールアドレスの詳細画面で「Webメールを使えるようにする」をオンにしてください。"
            action={
              <Button asChild>
                <AppLink to="/mailboxes">メールアドレスを見る</AppLink>
              </Button>
            }
          />
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">開くメールアドレスを選んでください</CardTitle>
            </CardHeader>
            <CardContent className="divide-y divide-border p-0">
              {items.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{m.address}</p>
                    {m.displayName ? (
                      <p className="truncate text-xs text-muted-foreground">{m.displayName}</p>
                    ) : null}
                  </div>
                  <Button
                    onClick={() => handleOpen(m.id)}
                    loading={open.isPending && open.variables === m.id}
                  >
                    <Mail aria-hidden />
                    開く
                    <ExternalLink aria-hidden />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <HelpNote title="Webメールとメールソフトの違い">
          Webメールはブラウザだけで使えるので、外出先や共用パソコンからでもメールを確認できます。
          Outlook や iPhone のメールアプリを使いたい場合は、
          <AppLink to="/mail-clients" className="mx-1 text-primary underline-offset-4 hover:underline">
            メールソフトの設定
          </AppLink>
          をご覧ください。どちらも同時に使えます。
        </HelpNote>
      </div>
    </>
  );
}
